/**
 * Frontend-only guard against the schemapack "Received type object" crash.
 *
 * cc-client-sdk's inbound listener calls `schemaTypeDict[msgType].decode(data)`
 * for any registered type, BEFORE our handler runs — so we can't catch it in
 * the handler. The server binary-encodes envelopes, but socket.io delivers
 * them to the BROWSER as a serialized Node Buffer: `{type:'Buffer', data:[…]}`
 * (a plain JS object whose `.data` is the byte array). Under the browser
 * `buffer` polyfill, schemapack's `Buffer.from(thatObject)` throws
 * "Received type object" → uncaught TypeError inside the socket.io listener.
 *
 * Rather than patch the shared cc-client-sdk, we wrap the `decode` fn on the
 * schema entry the SDK created (`socket.schemaTypeDict[msgType]`). The wrapper:
 *   1. {type:'Buffer', data:[…]}            → rebuild bytes → decode
 *   2. real Buffer / ArrayBuffer / TA / str → decode as-is
 *   3. a genuine already-decoded object     → pass through unchanged
 * try/catch → null on any failure. Idempotent via the `__ccDecodeHardened` tag.
 *
 * `hardenDecode` targets a single type; `hardenAllDecoders` sweeps every type
 * currently in the socket's registry (covers types registered by OTHER hooks,
 * e.g. online_players_result from the user-search hook, without enumerating).
 * The wrapper is a faithful pass-through for correctly-decodable payloads, so
 * it's safe to apply to shared types too.
 */
export function hardenDecode(socket: any, msgType: string): void {
  try {
    const entry = socket?.schemaTypeDict?.[msgType];
    if (!entry || typeof entry.decode !== 'function') return;
    if (entry.__ccDecodeHardened) return;
    const originalDecode = entry.decode.bind(entry);
    entry.decode = (data: any) => {
      const isBufferJson =
        data !== null &&
        typeof data === 'object' &&
        data.type === 'Buffer' &&
        Array.isArray(data.data);
      const isBinary =
        (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) ||
        data instanceof ArrayBuffer ||
        ArrayBuffer.isView(data) ||
        typeof data === 'string';
      try {
        if (isBufferJson) {
          const bytes =
            typeof Buffer !== 'undefined'
              ? Buffer.from(data.data)
              : new Uint8Array(data.data);
          return originalDecode(bytes);
        }
        if (isBinary) {
          return originalDecode(data);
        }
        // Already a decoded plain object (not a Buffer wrapper) — return as-is.
        return data;
      } catch (err) {
        console.error('[FunChallenges] decode failed for', msgType, err);
        return null;
      }
    };
    entry.__ccDecodeHardened = true;
  } catch (err) {
    console.warn('[FunChallenges] hardenDecode failed for', msgType, err);
  }
}

/** Harden every decoder currently registered on the socket. */
export function hardenAllDecoders(socket: any): void {
  try {
    const dict = socket?.schemaTypeDict;
    if (!dict) return;
    Object.keys(dict).forEach(msgType => hardenDecode(socket, msgType));
  } catch (err) {
    console.warn('[FunChallenges] hardenAllDecoders failed', err);
  }
}
